const TITLE_SMALL_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'in', 'nor', 'of', 'on', 'or', 'per', 'the', 'to', 'via', 'with'
]);

const PRESERVE_CASE_KEY = /(email|url|gst|phone|mobile|id|uuid|token|password|uom|code|number|currency|status|slug|estimate_type|estimateType|work_type|workType|category)/i;
const TITLE_CASE_KEY = /(name|project|company|contact|city|state|country|role|label)/i;
const SENTENCE_CASE_KEY = /(description|specification|note|message|term|inclusion|exclusion|warranty|address|subject|reason|summary)/i;

const normalizeWhitespace = (value: string): string =>
  value.replace(/\s+/g, ' ').trim();

const toTitleCase = (value: string): string => {
  const words = normalizeWhitespace(value).toLowerCase().split(' ');
  return words
    .map((word, index) => {
      if (!word) return word;
      if (index > 0 && TITLE_SMALL_WORDS.has(word)) return word;
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
};

const toSentenceCase = (value: string): string => {
  const normalized = normalizeWhitespace(value);
  if (!normalized) return normalized;

  const firstLetterIndex = normalized.search(/[a-zA-Z]/);
  if (firstLetterIndex < 0) return normalized;

  const prefix = normalized.slice(0, firstLetterIndex);
  const rest = normalized.slice(firstLetterIndex);
  const sentence = prefix + rest.charAt(0).toUpperCase() + rest.slice(1);

  if (sentence.length > 24 && !/[.!?]$/.test(sentence)) {
    return `${sentence}.`;
  }
  return sentence;
};

const normalizeStringByKey = (key: string | undefined, value: string): string => {
  const cleaned = normalizeWhitespace(value);
  if (!cleaned) return cleaned;

  if (key && PRESERVE_CASE_KEY.test(key)) {
    if (/email/i.test(key)) return cleaned.toLowerCase();
    return cleaned;
  }
  if (key && TITLE_CASE_KEY.test(key)) {
    return toTitleCase(cleaned);
  }
  if (key && SENTENCE_CASE_KEY.test(key)) {
    return toSentenceCase(cleaned);
  }

  // Default: keep user intent, but fix leading capitalization for readable output.
  return toSentenceCase(cleaned);
};

export const normalizeTextValue = <T>(value: T, key?: string): T => {
  if (typeof value === 'string') {
    return normalizeStringByKey(key, value) as T;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => normalizeTextValue(entry, key)) as T;
  }

  if (value && typeof value === 'object') {
    const normalized: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => {
      normalized[childKey] = normalizeTextValue(childValue, childKey);
    });
    return normalized as T;
  }

  return value;
};
